import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Landmark, PiggyBank, FileCheck } from 'lucide-react';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import NationalLocalFunnel from '../../components/national/NationalLocalFunnel';
import NationalRelatedGuides from '../../components/national/NationalRelatedGuides';

const SITE = 'https://buildpilot.com.au';
const CANONICAL = `${SITE}/first-home-buyer-guide-australia`;

// National overview of first-home support. Specific amounts and caps are
// state-set, so this funnels DOWN to the state pages for live figures.
const SCHEMES = [
  { name: 'First Home Owner Grant (FHOG)', body: 'A state grant for building or buying a brand-new home. Amount and property caps are set by each state and territory.' },
  { name: 'First Home Guarantee', body: 'A federal scheme letting eligible buyers purchase with a 5% deposit and no LMI, within state price caps.' },
  { name: 'First Home Super Saver (FHSS)', body: 'Save a deposit inside super with tax advantages, then withdraw it for your first home. Federal, ATO-administered.' },
  { name: 'Stamp duty concessions', body: 'Most states reduce or waive stamp duty for first home buyers, usually with a price threshold. State-set.' },
];

const STEPS = [
  'Work out your real borrowing power with a broker before you shortlist anything.',
  'Check which grants and concessions you qualify for in your state (they stack).',
  'Decide: buy established, buy new, or build - each changes which help applies.',
  'Line up a builder or property, then confirm the numbers with every concession applied.',
];

const LOCAL = [
  { code: 'SA', state: 'South Australia', city: 'Adelaide', primary: { label: 'Adelaide first home buyers', to: '/first-home-buyer-adelaide' }, secondary: { label: 'Grants & concessions', to: '/first-home-owner-grant-sa-adelaide' } },
  { code: 'VIC', state: 'Victoria', city: 'Melbourne', primary: { label: 'Melbourne first home buyers', to: '/first-home-buyer-melbourne' }, secondary: { label: 'Grants & concessions', to: '/vic/grants' } },
];

const FirstHomeBuyerAustraliaPage = () => {
  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE },
      { '@type': 'ListItem', position: 2, name: 'Guides', item: `${SITE}/guides` },
      { '@type': 'ListItem', position: 3, name: 'First home buyer guide (Australia)', item: CANONICAL },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>First Home Buyer Guide Australia 2026: Grants, Schemes & Steps | BuildPilot</title>
        <meta name="description" content="A national first home buyer guide for 2026 - the grants and schemes (FHOG, First Home Guarantee, FHSS, stamp duty), how they work, the steps to take, and live help for your state." />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
        <link rel="canonical" href={CANONICAL} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={CANONICAL} />
        <meta property="og:title" content="First Home Buyer Guide Australia 2026" />
        <meta property="og:description" content="The grants and schemes, how they stack, the steps - then live first-home help for your state." />
        <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>
      </Helmet>

      <Header variant="light" />

      <main data-testid="national-fhb-page">
        <section className="border-b border-gray-100 bg-[#fafbfc]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
            <p className="text-[11px] font-semibold uppercase tracking-[0.04em] text-[#1d8efe] mb-3">National guide · Australia</p>
            <h1 className="text-[32px] sm:text-[44px] font-bold text-[#1d1d1f] tracking-tight leading-[1.1] mb-4">
              The first home buyer guide for Australia
            </h1>
            <p className="text-[16px] sm:text-[18px] text-[#6b7078] leading-[1.6] max-w-2xl">
              Buying or building your first home comes with more help than most people realise - if you know what to claim. Here's the national picture of the grants and schemes, how they fit together, and where to get the exact figures for your state.
            </p>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="flex items-center gap-2 mb-5">
            <Landmark className="w-5 h-5 text-[#1d8efe]" />
            <h2 className="text-[24px] sm:text-[30px] font-bold text-[#1d1d1f] tracking-tight">The help that's out there</h2>
          </div>
          <div className="space-y-3">
            {SCHEMES.map((s) => (
              <div key={s.name} className="rounded-2xl border border-gray-200 p-5" data-testid={`fhb-scheme-${s.name.split(' ')[0].toLowerCase()}`}>
                <h3 className="text-[16px] font-bold text-[#1d1d1f] mb-1">{s.name}</h3>
                <p className="text-[14px] text-[#6b7078] leading-[1.55]">{s.body}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 rounded-2xl border border-[#1d8efe]/20 bg-[#f0f6ff] p-5 flex items-start gap-3">
            <FileCheck className="w-5 h-5 text-[#1d8efe] mt-0.5 shrink-0" />
            <p className="text-[14px] text-[#1d1d1f] leading-[1.6]">Grants and caps are set by each state and change often. We keep the exact current figures on the state pages below - always check those before you budget.</p>
          </div>
        </section>

        <section className="bg-[#fafbfc] border-y border-gray-100">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
            <div className="flex items-center gap-2 mb-5">
              <PiggyBank className="w-5 h-5 text-[#1d8efe]" />
              <h2 className="text-[24px] sm:text-[30px] font-bold text-[#1d1d1f] tracking-tight">Your first four steps</h2>
            </div>
            <ol className="space-y-3">
              {STEPS.map((s, i) => (
                <li key={s} className="flex gap-3 text-[15px] text-[#1d1d1f] leading-[1.55]" data-testid={`fhb-step-${i + 1}`}>
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full gradient-blue-bg text-white text-[13px] font-bold">{i + 1}</span>
                  <span className="pt-0.5">{s}</span>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <NationalLocalFunnel
          funnelTestId="fhb-local-funnel"
          cardPrefix="fhb-local-"
          eyebrow="Your state's exact figures"
          heading="Get the grants that actually apply to you."
          intro="BuildPilot is live in South Australia and Victoria and expanding across Australia. Open your state for current grant amounts, caps and first-home help."
          states={LOCAL}
          crossLink={{ label: 'Thinking of building your first home? See the cost to build', to: '/cost-to-build-a-house-australia', testid: 'fhb-to-cost-link' }}
        />

        <NationalRelatedGuides currentSlug="/first-home-buyer-guide-australia" />
      </main>

      <Footer />
    </div>
  );
};

export default FirstHomeBuyerAustraliaPage;
